import {
	Avatar,
	BorderColors,
	BorderRadius,
	Button,
	ButtonSize,
	Flex,
	FlexChild,
	FontFamilies,
	ForegroundColors,
	Table,
	TextColors,
	Typography,
} from "@abel-technologies-as/components";
import { useWeeklyDigitalColumns } from "./Columns";
import { CheckMarkCircle, CircleClose } from "@/shared/icons";
import { DhcPayoutApprovalItem } from "@/modules/dhcs/types/Dhc.types";
import { useBatchRatePeriods, useRatePeriod } from "@/modules/dhcs/hooks";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { showNotifications } from "@/shared/utils";
import { useTranslation } from "react-i18next";

export type PayoutTableProps = {
	item: DhcPayoutApprovalItem;
	dhcId: string;
};

export const PayoutTable = ({ item }: PayoutTableProps) => {
	const { t } = useTranslation("dhc");
	const queryClient = useQueryClient();
	const { mutate: batchRate, isPending } = useBatchRatePeriods();
	const { mutate: ratePeriod } = useRatePeriod();

	const [rows, setRows] = useState(() =>
		item.periods.map((period) => ({
			id: period.id,
			dateRange: `${period.startDate} – ${period.endDate}`,
			product: period.productName,
			daysWorked: period.daysWorked,
			qaRating: period.qaRating,
			activitiesAdded: period.activitiesAdded,
			activitiesCompleted: period.activitiesCompleted,
			qaComment: period.qaComment,
			weekNumber: period.weekNumber,
			tasksAdded: period.tasksAdded,
			tasksCompleted: period.tasksCompleted,
			workoutsAdded: period.workoutsAdded,
			workoutsCompleted: period.workoutsCompleted,
		}))
	);

	const allRated = rows.every((row) => row.qaRating > 0);
	const unAllRated = rows.every((row) => row.qaRating === 0);

	const onSuccess = () => {
		showNotifications({ title: "Periods updated", message: "Ratings saved successfully." });
		queryClient.invalidateQueries({ queryKey: ["dhc-payout-approval"] });
	};

	const onError = () => {
		showNotifications({ title: "Error", message: "Failed to save ratings.", successNotification: false });
	};

	useEffect(() => {
		setRows(
			item.periods.map((period) => ({
				id: period.id,
				dateRange: `${period.startDate} – ${period.endDate}`,
				product: period.productName,
				daysWorked: period.daysWorked,
				qaRating: period.qaRating,
				activitiesAdded: period.activitiesAdded,
				activitiesCompleted: period.activitiesCompleted,
				qaComment: period.qaComment,
				weekNumber: period.weekNumber,
				tasksAdded: period.tasksAdded,
				tasksCompleted: period.tasksCompleted,
				workoutsAdded: period.workoutsAdded,
				workoutsCompleted: period.workoutsCompleted,
			}))
		);
	}, [item.periods]);

	const handleRate = (disabled: boolean, qaRating: number) => {
		if (disabled) return;

		const periods = rows
			.filter((item) => item.qaRating !== qaRating)
			.map((period) => ({
				id: period.id,
				qaRating,
				qaComment: period.qaComment ?? "",
			}));

		batchRate(periods, {
			onSuccess: () => {
				setRows((prev) => {
					return prev.map((row) => ({ ...row, qaRating }));
				});
				onSuccess();
			},
			onError,
		});
	};

	const handleToggle = (id: number) => {
		setRows((prev) =>
			prev.map((row) => {
				if (row.id !== id) return row;
				const newRating = row.qaRating ? 0 : 100;
				ratePeriod(
					{ id, qaRating: newRating, qaComment: row.qaComment ?? "" },
					{
						onSuccess: () => showNotifications({ title: t("period_updated"), message: t("rating_saved_successfully") }),
						onError: () =>
							showNotifications({ title: t("error"), message: t("failed_to_save_rating"), successNotification: false }),
					}
				);
				return { ...row, qaRating: newRating };
			})
		);
	};

	return (
		<Table
			cellPaddingTopBottom={"13px"}
			headerContent={
				<Flex justifyContent="space-between">
					<Flex gap="10px">
						<Avatar size={40} imageUrl={item.userPhoto} />
						<FlexChild flexBasis="1" flexGrow={1} flexShrink={1}>
							<Flex flexDirection="column" alignItems="flex-start">
								<Flex gap="8px" justifyContent="space-between">
									<Typography
										color={TextColors.Secondary}
										fontWeight={600}
										fontSize="18px"
										fontFamily={FontFamilies.InterSemiBold}>
										{item.name}
									</Typography>
								</Flex>

								<Typography
									fontWeight={400}
									fontSize="16px"
									color={TextColors.Tertiary}
									fontFamily={FontFamilies.InterRegular}
									textAlign="left">
									{item.email}
								</Typography>
							</Flex>
						</FlexChild>
					</Flex>
					<Flex gap="12px">
						<Button
							borderSize={"1px"}
							size={ButtonSize.MEDIUM}
							borderRadius={`${BorderRadius.MD}px`}
							iconBefore={<CircleClose width="20px" height="20px" color={TextColors.Secondary} />}
							boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
							borderColor={BorderColors.Primary}
							disabled={isPending || unAllRated}
							onClick={() => handleRate(unAllRated, 0)}>
							<Typography
								margin="0 0 0 4px"
								color={ForegroundColors.Secondary}
								fontSize="14px"
								fontWeight={600}
								lineHeight="18px"
								fontFamily={FontFamilies.InterSemiBold}>
								{t("reject_period")}
							</Typography>
						</Button>
						<Button
							borderSize={"1px"}
							size={ButtonSize.MEDIUM}
							borderRadius={`${BorderRadius.MD}px`}
							iconBefore={
								<CheckMarkCircle
									width="20px"
									height="20px"
									color={allRated ? ForegroundColors.Quinary : TextColors.BrandUtility700}
								/>
							}
							boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
							borderColor={allRated ? BorderColors.Secondary : BorderColors.Secondary_primary}
							disabled={isPending}
							onClick={() => handleRate(allRated, 100)}>
							<Typography
								margin="0 0 0 4px"
								color={allRated ? ForegroundColors.Quinary : TextColors.BrandUtility700}
								fontSize="14px"
								fontWeight={600}
								lineHeight="18px"
								fontFamily="Inter-SemiBold">
								{t("approve_period")}
							</Typography>
						</Button>
					</Flex>
				</Flex>
			}
			columns={useWeeklyDigitalColumns({ onToggle: handleToggle })}
			rows={rows}
		/>
	);
};
